import React, { useState, useEffect } from 'react';
import { Popover, Select, Switch, message, Modal } from 'antd';
import { CheckOutlined, BellOutlined, MonitorOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import IntlMessages from '../Locale/IntlMessages';
import { notifyApi } from '@/services/notify/notify-api';
import { useAuthStore } from '../../store/useAuthStore';
import { employeeApi } from '@/services/employee/employee-api';

interface NotificationSettingsDropdownProps {
    children: React.ReactNode;
    onDesktopNotificationChange?: (enabled: boolean) => void;
    defaultDesktopNotification?: boolean;
}

export const NotificationSettingsDropdown: React.FC<NotificationSettingsDropdownProps> = ({
    children,
    onDesktopNotificationChange,
    defaultDesktopNotification = false,
}) => {
    const router = useRouter();
    const { profile, tenantID, setProfile } = useAuthStore();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [emailFrequency, setEmailFrequency] = useState(profile?.tanSuatGuiMail || 'khongbaogio');
    const [emailFrequencySaving, setEmailFrequencySaving] = useState(false);
    const [desktopNotification, setDesktopNotification] = useState(defaultDesktopNotification);

    useEffect(() => {
        setEmailFrequency(profile?.tanSuatGuiMail || 'khongbaogio');
    }, [profile?.tanSuatGuiMail]);

    // Check browser notification support and permission on mount
    useEffect(() => {
        if (typeof window !== 'undefined' && 'Notification' in window) {
            // Check if permission was previously granted
            const savedPermission = localStorage.getItem('desktopNotificationEnabled');
            if (savedPermission === 'true' && Notification.permission === 'granted') {
                setDesktopNotification(true);
            } else if (Notification.permission === 'granted') {
                setDesktopNotification(true);
            } else if (Notification.permission === 'denied') {
                setDesktopNotification(false);
            }
        }
    }, []);

    const handleMarkAllAsRead = () => {
        Modal.confirm({
            title: 'Xác nhận',
            content: 'Bạn có chắc chắn muốn đánh dấu tất cả thông báo là đã đọc?',
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            onOk: async () => {
                await setReadAllNotification();
                setPopoverOpen(false);
            },
        });
    };

    const setReadAllNotification = async () => {
        try {
            const toID = profile?.id;
            if (!toID) return;

            const res: any = await notifyApi.setReadAll({ toID, action: null });

            if (res?.success) {
                message.success('Đã đánh dấu tất cả thông báo là đã đọc');
            } else {
                message.error('Đánh dấu tất cả thông báo là đã đọc thất bại');
            }

        } catch (err) {
            console.warn('loadNotifyData failed:', err);
        }
    };

    const handleEmailFrequencyChange = async(value: string) => {
        const toID = profile?.id;
        if (!toID || emailFrequencySaving) return;

        const previousFrequency = emailFrequency;
        setEmailFrequency(value);
        setEmailFrequencySaving(true);
        try {
            const res = await employeeApi.setTanSuat({id: toID, tanSuatGuiMail: value }) as unknown as {
                success?: boolean;
                data?: Parameters<typeof setProfile>[0];
            };

            if (res?.success) {
                if (res?.data) setProfile(res.data);
                message.success('Đã cập nhật tần suất thông báo qua email');
            } else {
                setEmailFrequency(previousFrequency);
                message.error('Cập nhật tần suất thông báo qua email thất bại');
            }
        } catch (err) {
            setEmailFrequency(previousFrequency);
            message.error('Cập nhật tần suất thông báo qua email thất bại');
            console.warn('handleEmailFrequencyChange failed:', err);
        } finally {
            setEmailFrequencySaving(false);
        }
    };

    const handleDesktopNotificationChange = async (checked: boolean) => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            message.warning('Trình duyệt của bạn không hỗ trợ thông báo desktop');
            return;
        }

        if (checked) {
            // Request permission if not already granted
            if (Notification.permission === 'default') {
                try {
                    const permission = await Notification.requestPermission();
                    if (permission === 'granted') {
                        setDesktopNotification(true);
                        localStorage.setItem('desktopNotificationEnabled', 'true');
                        onDesktopNotificationChange?.(true);
                        message.success('Đã bật thông báo desktop');

                        // Show a test notification
                        new Notification('Thông báo đã được bật', {
                            body: 'Bạn sẽ nhận được thông báo từ hệ thống',
                            icon: '/favicon.ico', // Adjust icon path as needed
                        });
                    } else if (permission === 'denied') {
                        setDesktopNotification(false);
                        localStorage.setItem('desktopNotificationEnabled', 'false');
                        message.warning('Quyền thông báo đã bị từ chối. Vui lòng bật trong cài đặt trình duyệt.');
                    }
                } catch (error) {
                    console.error('Error requesting notification permission:', error);
                    message.error('Không thể yêu cầu quyền thông báo');
                    setDesktopNotification(false);
                }
            } else if (Notification.permission === 'granted') {
                setDesktopNotification(true);
                localStorage.setItem('desktopNotificationEnabled', 'true');
                onDesktopNotificationChange?.(true);
            } else if (Notification.permission === 'denied') {
                setDesktopNotification(false);
                message.warning('Quyền thông báo đã bị từ chối. Vui lòng bật trong cài đặt trình duyệt.');
            }
        } else {
            // Turn off notification
            setDesktopNotification(false);
            localStorage.setItem('desktopNotificationEnabled', 'false');
            onDesktopNotificationChange?.(false);
        }
    };

    const handleOpenNotificationPage = () => {
        // Navigate to notification page - adjust route as needed
        router.push('/thong-bao');
        setPopoverOpen(false);
    };

    const popoverContent = (
        <div className="bg-white rounded-xl border border-gray-200 p-4 w-[280px] space-y-4">
            {/* Mark all as read */}
            <div
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors"
            >
                <CheckOutlined className="text-gray-600" />
                <span className="text-sm text-gray-700">
                    <IntlMessages id="notify.markAllAsRead" text="Đánh dấu tất cả là đã đọc" />
                </span>
            </div>

            {/* Email notification frequency */}
            <div className="space-y-2">
                <div className="text-sm font-bold text-gray-700">
                    <IntlMessages id="notify.emailFrequency" text="Tần suất thông báo qua email" />
                </div>
                <Select
                    value={emailFrequency}
                    onChange={handleEmailFrequencyChange}
                    disabled={emailFrequencySaving}
                    className="w-full"
                    size="middle"
                    options={[
                        { value: 'ngaylaptuc', label: <IntlMessages id="notify.ngaylaptuc" text="Ngay lập tức" /> },
                        { value: 'khongbaogio', label: <IntlMessages id="notify.khongbaogio" text="Không bao giờ" /> },
                        { value: 'dinhky', label: <IntlMessages id="notify.dinhky" text="Định kỳ" /> },
                    ]}
                />
            </div>

            {/* Desktop notification */}
            <div className="flex items-center justify-between hover:bg-gray-50 p-2 rounded-md transition-colors">
                <div className="flex items-center gap-2">
                    <BellOutlined className="text-gray-600" />
                    <span className="text-sm text-gray-700">
                        <IntlMessages id="notify.allowDesktop" text="Cho phép thông báo trên Desktop" />
                    </span>
                </div>
                <Switch checked={desktopNotification} onChange={handleDesktopNotificationChange} size="small" />
            </div>

            {/* Open notification page */}
            <div
                onClick={handleOpenNotificationPage}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors"
            >
                <MonitorOutlined className="text-gray-600" />
                <span className="text-sm text-gray-700">
                    <IntlMessages id="notify.openNotificationPage" text="Mở trang thông báo" />
                </span>
            </div>
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            trigger="click"
            placement="bottomRight"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            overlayClassName="notification-settings-popover"
        >
            {children}
        </Popover>
    );
};
